import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useNavigate, useParams } from "react-router";
import { useMedicaoFilters } from "../hooks/medicao_terceirizacao/filter";
import { useMedicaoPagination } from "../hooks/medicao_terceirizacao/pagination";
import { MedicaoTerceirizacaoLista } from "../../domain/models/medicao_terceirizacao_lista";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

type LoadMedicaoFuncState = {
  IdMedicaoTerceirizacao?: number;
  page?: number;
  rowsPerPage?: number;
  centroDeCusto?: number[],
  centroDeCustoLista?: CentroCustoDropDown[] | undefined,
  FornecedorPesquisa?: number,
  ContratoPesquisa?: number,
  NMedicaoPesquisa?: string, 
  StatusPesquisa?: number, 
};

export const useMedicaoListaViewModel = () => {
  const {
    commons: {  },
    reports: { getReportMedicaoTerceirizacaoUseCase },
    terceirizacao: {getAllListagemMedicaoUseCase, deleteMedicaoUseCase},
  } = useDependencyInjection();
  const debouncedRef = useCreateDebounceHandler(750);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [openLegendaModal, setOpenLegendaModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [MedicaoLista, setMedicaoLista] = useState<MedicaoTerceirizacaoLista[]>([]);
  const [selectedMedicao, setSelectedMedicao] = useState<MedicaoTerceirizacaoLista>();
  const [MedicaoDeleteModal, setMedicaoDeleteModal] = useState<boolean>(false);
  const {pushErrorNotification } = usePageLoader();
  
  const filters = useMedicaoFilters();
  const pagination = useMedicaoPagination();
  const navigate = useNavigate();
  const { s } = useParams();

  const {
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  //#region Relatorio
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  //#endregion
  

  // #region Funções de carregamento
  const loadMedicaoListaState = useCallback(async (funcState?: LoadMedicaoFuncState) => {
    try {
      if(s !== undefined && s !== null){
        filters.setStatusPesquisa(Number(s));
        filters.StatusPesquisa = Number(s);
      } 

      const filterID = evaluateParamValue(funcState?.IdMedicaoTerceirizacao, filters.IdMedicaoTerceirizacao);
      const filterFornecedor = funcState?.FornecedorPesquisa ?? filters.FornecedorPesquisa;
      const filterContrato = funcState?.ContratoPesquisa ?? filters.ContratoPesquisa;
      const filterStatusPesquisa = funcState?.StatusPesquisa ?? filters.StatusPesquisa;
      const filterNMedicaoPesquisa = funcState?.NMedicaoPesquisa ?? filters.NMedicaoPesquisa;
      const filterCentroDeCusto = funcState?.centroDeCusto ?? filters.centroDeCusto;
      const page = funcState?.page ?? pagination.page;
      const rows = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllListagemMedicaoUseCase.getAllListagemMedicao({
          IdMedicaoTerceirizacao: filterID,
          centroDeCusto: filterCentroDeCusto,
          FornecedorPesquisa: filterFornecedor,
          NMedicaoPesquisa: filterNMedicaoPesquisa,
          StatusPesquisa: filterStatusPesquisa,
          ContratoPesquisa: filterContrato
        },
        page,
        rows
      );
      setMedicaoLista(getAllListagemMedicaoUseCase.items);
      pagination.setRowsPerPage(getAllListagemMedicaoUseCase.pageSize);
      pagination.setTotalItems(getAllListagemMedicaoUseCase.totalItems);
    } catch (e: unknown) {
        throw new Error("Falha ao buscar Medições!");
    }
  }, [filters, getAllListagemMedicaoUseCase, pagination, s]);
  
  //#endregion

  // #region useEffect
  
  usePageStartBootstrap(async () => {
    try {
      await loadMedicaoListaState();
      filters.clearFilters();
      await loadCentroCustoDropDown(list => useMedicaoFilters.setState({ centroDeCustoLista: list }));
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadMedicaoListaState();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadMedicaoListaState, pushErrorNotification]);
  //#endregion
  
  // #region Eventos
  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadMedicaoListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadMedicaoListaState, pagination, pushErrorNotification]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
        await loadMedicaoListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadMedicaoListaState, pagination, pushErrorNotification]);

  const handleCadastroMedicaoOpenState = useCallback((IdMedicaoTerceirizacao: number | null, openState: boolean) => {
    if (openState) {
      if (IdMedicaoTerceirizacao) {
        navigate(`/MedicaoTerceirizacao_cadastro/${IdMedicaoTerceirizacao}`)
      } 
    }
  }, [navigate]);

  //#endregion


  //#region Filtros
  const handleClearFilters = useCallback(async () => {
    try {
        setLoading(true);
        filters.clearFilters();
        pagination.setPage(1);
        navigate('/MedicaoTerceirizacao_lista');
        await loadMedicaoListaState();
    } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar Medicaos!'));
    } finally {
        setLoading(false);
    }
  }, [filters, loadMedicaoListaState, navigate, pagination, pushErrorNotification]);

  const handleFornecedorChange = useDebouncedCallback(debouncedRef, async (newValue: number) => {
    useMedicaoFilters.getState().setFornecedorPesquisa(newValue);

    try {
      setLoading(true);
      useMedicaoPagination.getState().setPage(1);
      await loadMedicaoListaState({ FornecedorPesquisa: newValue, page: 1 } as any);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Medicao!"));
    } finally {
      setLoading(false);
    }

    return () => {};
  }, [pushErrorNotification, loadMedicaoListaState]);

  const handleContratoChange = useDebouncedCallback(debouncedRef, async (newValue: number) => {
    useMedicaoFilters.getState().setContratoPesquisa(newValue);

    try {
      setLoading(true);
      useMedicaoPagination.getState().setPage(1);
      await loadMedicaoListaState({ ContratoPesquisa: newValue, page: 1 } as any);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Medicao!"));
    } finally {
      setLoading(false);
    }

    return () => {};
  }, [pushErrorNotification, loadMedicaoListaState]);

  const handleCentroDeCustoChange = useDebouncedCallback(debouncedRef, (newValue: number[]) => {
    useMedicaoFilters.getState().setCentroDeCusto(newValue);
    return async () => {
      try {
        setLoading(true);
        useMedicaoPagination.getState().setPage(1);
        await loadMedicaoListaState({ centroDeCusto: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Medicao!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadMedicaoListaState, pushErrorNotification, ]);


  const handleNMedicaoCampoChange = useDebouncedCallback(debouncedRef, (newValue: string) => {
    useMedicaoFilters.getState().setNMedicaoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useMedicaoPagination.getState().setPage(1);
        await loadMedicaoListaState({ NMedicaoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Diario de Obras!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadMedicaoListaState, pushErrorNotification,]);

  const handleStatusPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useMedicaoFilters.getState().setStatusPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useMedicaoPagination.getState().setPage(1);
        await loadMedicaoListaState({ StatusPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Diario de Obras!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadMedicaoListaState, pushErrorNotification,]);
  //#endregion

  //#region Funções de deletar
  const handleDeleteMedicaoModalOpenState = useCallback((IdMedicaoTerceirizacao: number | null, openState: boolean) => {
    if (openState) {
      if (IdMedicaoTerceirizacao && MedicaoLista) {
        setSelectedMedicao(MedicaoLista.find(x => x.IdMedicaoTerceirizacao === IdMedicaoTerceirizacao));
      } else {
        setSelectedMedicao(undefined);
      }
    }
    setMedicaoDeleteModal(openState);
  }, [MedicaoLista]);

  const handleDeleteConfirmaMedicao = useCallback(async () => {
    if (selectedMedicao) {
      try {
        setSaving(true);
        await deleteMedicaoUseCase.deleteMedicao(selectedMedicao.IdMedicaoTerceirizacao);
        await loadMedicaoListaState();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
      } finally {
        setSaving(false);
        setMedicaoDeleteModal(false);
      }
    }
  },[selectedMedicao, deleteMedicaoUseCase, loadMedicaoListaState, pushErrorNotification]);
  
  //#endregion

  const handleShowLegenda = useCallback((openState: boolean) => {
      setOpenLegendaModal(openState);
  }, []);

  //#region  relatorio
  const handleReportMedicaoTerceirizacao = useCallback(async (id: number) => {
    try {
      setLoading(true);
   
      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportMedicaoTerceirizacaoUseCase.getReportMedicaoTerceirizacao(requiredMimeType, id),
      });

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatorio de medição!'))
    } finally {
      setLoading(false);
    }
  }, [getReportMedicaoTerceirizacaoUseCase, pushErrorNotification, startReportViewerProcess]);

  //#endregion
  
  
  
  return {
    loading,
    errorMessage,
    saving,
    pagination,
    filters,
    MedicaoLista,
    selectedMedicao,
    MedicaoDeleteModal,
    openLegendaModal,
    reportViewerState,
    handlePageChange,
    handleRowsPerPageChange,
    handleReload,
    handleClearFilters,
    handleFornecedorChange,
    handleCentroDeCustoChange,
    handleNMedicaoCampoChange,
    handleStatusPesquisaChange,
    handleDeleteConfirmaMedicao,
    handleDeleteMedicaoModalOpenState,
    handleCadastroMedicaoOpenState,
    handleContratoChange,
    handleShowLegenda,
    handleReportMedicaoTerceirizacao,
  };
};